import React, { FC, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { IconButton, Menu, MenuItem, Tooltip, TextField } from '@mui/material';
import { MoreVert } from '@mui/icons-material';

import { useBoundStore } from 'store';
import { LocalizationContext, UserContext } from 'contexts';

export const Header: FC<any> = ({
  col,
  columnIndex,
  rowSpan,
  colSpan,
  onAddColumn,
  onDeleteColumn,
  onHeaderChange,
  onSort,
  modificationColor,
}) => {
  const location = useLocation();
  const { userPermissions } = useContext(UserContext);

  const { writeMode, draftPreviewMode } = useBoundStore((state) => ({
    writeMode: state.writeMode,
    draftPreviewMode: state.draftPreviewMode,
  }));

  const { dictionary } = useContext(LocalizationContext);
  const [isHovered, setIsHovered] = useState(false);
  const [anchorEl, setAnchorEl] = useState<any>();

  const [localValue, setLocalValue] = useState(col.label || '');

  useEffect(() => {
    setLocalValue(col.label || '');
  }, [col.label]);

  const handleBlur = () => {
    if (onHeaderChange && localValue !== col.label) {
      onHeaderChange(columnIndex, localValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  const isPrintPreview = location.pathname.includes('print-preview');
  const isEditable = writeMode && !!userPermissions?.change_block && !draftPreviewMode && !isPrintPreview;

  return (
    <th
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'relative',
        minWidth: isEditable ? 100 : 24,
        width: col.width,
        backgroundColor: modificationColor || '#273e71',
        color: 'white',
        padding: 10,
        border: '1px #fff solid',
        verticalAlign: 'middle',
      }}
      rowSpan={rowSpan}
      colSpan={colSpan}
    >
      {isEditable ? (
        <TextField
          variant="standard"
          fullWidth
          value={localValue}
          placeholder={dictionary.editableTable.noLabel}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          InputProps={{
            disableUnderline: true,
            style: {
              color: 'white',
              fontSize: 'inherit',
              fontWeight: 'inherit',
              textAlign: 'center',
            },
          }}
          sx={{
            '& .MuiInputBase-input': {
              textAlign: 'center',
              padding: 0,
              '&::placeholder': {
                color: 'rgba(255, 255, 255, 0.5)',
                fontStyle: 'italic',
                opacity: 1,
              },
            },
          }}
        />
      ) : (
        <span>
          {(col.label || '').split('\n').map((line: string, i: number) => (
            <React.Fragment key={i}>
              {line}
              {i !== (col.label || '').split('\n').length - 1 && <br/>}
            </React.Fragment>
          ))}
        </span>
      )}

      {(isEditable && onAddColumn && onDeleteColumn) && (
        <div
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{ position: 'absolute', right: 3, top: 3, opacity: isHovered || Boolean(anchorEl) ? 1 : 0 }}
        >
          <Tooltip title={dictionary.editableTable.columnActions}>
            <IconButton size="small" onClick={e => setAnchorEl(e.currentTarget)}>
              <MoreVert fontSize="small" style={{ fill: '#fff' }}/>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => {
              setAnchorEl(null);
              setIsHovered(false);
            }}
          >
            {onAddColumn && [
              <MenuItem key="add-column-before" onClick={() => {
                onAddColumn(columnIndex);
                setAnchorEl(null);
              }}>{dictionary.editableTable.addColumnBefore}</MenuItem>,
              <MenuItem key="add-column-after" onClick={() => {
                onAddColumn(columnIndex + 1);
                setAnchorEl(null);
              }}>{dictionary.editableTable.addColumnAfter}</MenuItem>,
              <MenuItem key="duplicate-column" onClick={() => {
                onAddColumn(columnIndex, true);
                setAnchorEl(null);
              }}>{dictionary.editableTable.duplicateColumn}</MenuItem>,
            ]}
            {onDeleteColumn && (
              <MenuItem onClick={() => {
                onDeleteColumn(columnIndex);
                setAnchorEl(null);
              }}>{dictionary.editableTable.deleteColumn}</MenuItem>
            )}
            {onSort && [
              <MenuItem key="sort-rows" onClick={() => {
                onSort(columnIndex, 'rows');
                setAnchorEl(null);
              }}>{dictionary.editableTable.sortRows}</MenuItem>,
              <MenuItem key="sort-columns" onClick={() => {
                onSort(columnIndex, 'columns');
                setAnchorEl(null);
              }}>{dictionary.editableTable.sortColumns}</MenuItem>,
            ]}
          </Menu>
        </div>
      )}
    </th>
  );
};